import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs, { Dayjs } from 'dayjs'
import { Language } from '../../../types/user/User'
import { DATE_FORMATS, DateFormat, parseDate } from '../../../config/dayjsConfig'

/**
 * Hook that provides a localized date formatter function
 *
 * Returns a function that formats dates according to the current user's language settings
 *
 * @returns A formatter function that takes a date and format key and returns a formatted string
 *
 * @example
 * ```tsx
 * const dateFormatter = useDateFormatter()
 * const formatted = dateFormatter(new Date(), 'standard') // '18.12.2025' (if Finnish)
 * const withTime = dateFormatter(someDate, 'standardWithTime') // '18.12.2025 14:30'
 * ```
 */
export const useDateFormatter = () => {
  const { i18n } = useTranslation()
  const currentLanguage = (i18n.language || Language.fi) as Language

  return useCallback(
    (date: string | Date | Dayjs | null | undefined, formatKey: keyof DateFormat = 'standard'): string => {
      if (!date) return ''

      // Get the format string for current language
      const formatString = DATE_FORMATS[currentLanguage]?.[formatKey] || DATE_FORMATS[Language.fi][formatKey]

      // Handle different date types
      let dayjsDate: Dayjs | null = null

      if (dayjs.isDayjs(date)) {
        dayjsDate = date
      } else if (date instanceof Date) {
        dayjsDate = dayjs(date)
      } else if (typeof date === 'string') {
        dayjsDate = parseDate(date)
      }

      if (!dayjsDate || !dayjsDate.isValid()) {
        return ''
      }

      // Format with the appropriate locale
      const localizedDate = dayjsDate.locale(currentLanguage)
      return localizedDate.format(formatString)
    },
    [currentLanguage]
  )
}
